import { find, forEach, prop } from 'ramda';
import { isArray } from 'ramda-adjunct';
import { IMediaManagerSDKPaging, IMediaManagerSDKRequestOptions, MediaManagerSDKAnyItem } from '../media-manager-sdk';
import { uniqueRandomId } from '../uniqueId';
import { DataProvider } from './DataProvider';

interface IMediaManagerSDKRequestOptionsWithSizeRequired extends IMediaManagerSDKRequestOptions {
  paging: IMediaManagerSDKPaging & { size: number };
}

export class ItemsLoader {
  readonly dataProviders: DataProvider[];
  readonly id: string;

  constructor(dataProviders: DataProvider[] = []) {
    this.dataProviders = dataProviders;
    this.id = uniqueRandomId('dataLoader-');
  }

  get hasMoreItems() {
    const nearestDataProvider = this.nearestDataProvider;

    return Boolean(nearestDataProvider && nearestDataProvider.hasMoreItems);
  }

  reset(): void {
    forEach((provider: DataProvider) => {
      provider.reset();
    }, this.dataProviders);
  }

  cancel(): void {
    forEach((provider: DataProvider) => {
      provider.cancel();
    }, this.dataProviders);
  }

  async loadItems(
    folderId: string | null,
    options: IMediaManagerSDKRequestOptionsWithSizeRequired,
    totalItemsLoaded = 0,
  ): Promise<MediaManagerSDKAnyItem[]> {
    const nearestDataProvider = this.nearestDataProvider;
    const pagingOptions = options.paging;
    const needItemsCount = pagingOptions.size;

    if (nearestDataProvider) {
      let response;
      try {
        response = await nearestDataProvider.loadItems(folderId, options);
      } catch (error) {
        return Promise.reject(error);
      }

      if (!isArray(response.data)) {
        return [];
      }
      const itemsInThisResponse = Number(response.data.length);
      const totalItemsAfterThisResponse = totalItemsLoaded + itemsInThisResponse;

      if (
        totalItemsAfterThisResponse >= needItemsCount ||
        !this.nearestDataProvider ||
        !this.nearestDataProvider.hasMoreItems
      ) {
        return response.data;
      }

      let additionalItems;
      try {
        additionalItems = await this.loadItems(folderId, options, totalItemsAfterThisResponse);
      } catch (error) {
        return Promise.reject(error);
      }
      return [...response.data, ...additionalItems];
    }

    return [];
  }

  private get nearestDataProvider(): DataProvider {
    return find<DataProvider>(prop('hasMoreItems'), this.dataProviders);
  }
}
